import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLIC_KEY } from 'Api';
import { registerGiftUser } from 'Api/apiFunctions';
import { Logo } from 'Components';
import SuccessPopUp from 'Components/VideoPopup/SuccessPopUp';
import styles from './index.module.css';

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_PUBLIC_KEY);

// Stripe Payment Form Component
const GiftPaymentForm = ({ clientSecret, giftUserUid, planId, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [isPaymentReady, setIsPaymentReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setProcessing(true);
    setPaymentError('');

    try {
      console.log('🔄 Starting payment confirmation...');
      const result = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      });

      console.log('✅ Stripe payment result:', result);

      if (result.error) {
        console.error('❌ Payment failed:', result.error);
        setPaymentError(result.error.message);
        setProcessing(false);
        if (onError) onError(result.error.message);
      } else {
        console.log('💳 Payment successful!');
        console.log('📝 PaymentIntent ID:', result.paymentIntent.id);
        console.log('🔑 Client Secret (checkout_id):', clientSecret);
        console.log('💰 Amount:', result.paymentIntent.amount);
        console.log('💵 Currency:', result.paymentIntent.currency);
        console.log('✅ Status:', result.paymentIntent.status);

        // Payment succeeded - register gift user
        try {
          console.log('📤 Calling registerGiftUser API with:', {
            uid: giftUserUid,
            checkout_id: clientSecret,
            plan_id: parseInt(planId, 10)
          });

          const response = await registerGiftUser({
            uid: giftUserUid,
            checkout_id: clientSecret,
            plan_id: parseInt(planId, 10)
          });

          console.log('📥 registerGiftUser response:', response);

          if (response.data?.success) {
            // Save auth token from response
            const userToken = response.data?.data?.userDetails?.[0]?.token;
            if (userToken) {
              localStorage.setItem('token', userToken);
            }
            // Save uploader token (access) from api_response
            const uploaderToken = response.data?.api_response?.access;
            if (uploaderToken) {
              localStorage.setItem('uploader_token', uploaderToken);
              // Parse and store JWT expiry so tokenManager doesn't think it's expired
              try {
                const payload = JSON.parse(atob(uploaderToken.split('.')[1]));
                if (payload.exp) {
                  localStorage.setItem('uploader_token_expiry', (payload.exp * 1000).toString());
                }
              } catch (e) { console.warn('[GIFT_PAYMENT] Failed to parse uploader token expiry:', e); }
            }
            console.log('✅ Gift user registered successfully!');
            onSuccess();
          } else {
            console.error('❌ Registration failed:', response.data?.message);
            const errorMsg = 'Payment successful but registration failed. Please contact support.';
            setPaymentError(errorMsg);
            setProcessing(false);
            if (onError) onError(errorMsg);
          }
        } catch (err) {
          console.error('❌ Registration API error:', err);
          const errorMsg = 'Payment successful but registration failed. Please contact support.';
          setPaymentError(errorMsg);
          setProcessing(false);
          if (onError) onError(errorMsg);
        }
      }
    } catch (err) {
      console.error('❌ Payment confirmation error:', err);
      setPaymentError('Payment failed. Please try again.');
      setProcessing(false);
      if (onError) onError('Payment failed. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className={styles.paymentForm}>
      <div className={styles.paymentElementWrapper}>
        <PaymentElement
          onChange={(event) => setIsPaymentReady(event.complete)}
          options={{
            layout: 'tabs',
            paymentMethodOrder: ['card', 'apple_pay'],
            wallets: { googlePay: 'never' },
          }}
        />
      </div>

      {paymentError && (
        <div className={styles.errorMessage}>{paymentError}</div>
      )}

      <button
        type="submit"
        disabled={!stripe || !isPaymentReady || processing}
        className={styles.payButton}
      >
        {processing ? 'Processing...' : 'Complete Payment'}
      </button>
    </form>
  );
};

export const GiftPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [giftExpressData, setGiftExpressData] = useState(null);
  const { clientSecret, giftUserUid, planId, planAmount, planName } = location.state || {};

  // Clear existing auth tokens on first load so new tokens are saved fresh
  useEffect(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('uploader_token');
    localStorage.removeItem('uploader_token_expiry');
  }, []);

  useEffect(() => {
    // Redirect if missing required data
    if (!clientSecret || !giftUserUid || !planId) {
      navigate('/gift');
    }
  }, [clientSecret, giftUserUid, planId, navigate]);

  const handlePaymentSuccess = () => {
    // Prepare data in format expected by SuccessPopUp
    const expressData = {
      plan_name: planName || 'Gift Plan',
      is_trial: false,
      plan_duration: '730', // 2 years = 730 days
      datetime: new Date().toISOString(),
    };

    setGiftExpressData(expressData);
    setShowSuccessModal(true);

    // Navigate to access page after 3 seconds
    setTimeout(() => {
      navigate('/access');
    }, 3000);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  if (!clientSecret) {
    return null;
  }

  return (
    <div className={styles.paymentPage}>
      <div className={styles.header}>
        <Logo className={styles.logo} isLarge isLight />
      </div>

      <div className={styles.paymentContainer}>
        <div className={styles.paymentContent}>
          <h1 className={styles.heading}>Complete Your Payment</h1>

          {planName && planAmount && (
            <div className={styles.orderSummary}>
              <h2>Order Summary</h2>
              <div className={styles.summaryRow}>
                <span>{planName}</span>
                <span>
                  $
                  {planAmount}
                </span>
              </div>
            </div>
          )}

          <div className={styles.formSection}>
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <GiftPaymentForm
                clientSecret={clientSecret}
                giftUserUid={giftUserUid}
                planId={planId}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>
          </div>
        </div>
      </div>

      {/* Success Popup with background image */}
      {showSuccessModal && giftExpressData && (
        <SuccessPopUp
          expressData={giftExpressData}
          paymentStatus="success"
          isGift
          onHide={() => {
            setShowSuccessModal(false);
            navigate('/access');
          }}
          paymentNavigation={(path) => navigate(path || '/access')}
          setOpenPaymentLoader={() => {}}
        />
      )}
    </div>
  );
};

export default GiftPayment;
